import { Query } from "@cubejs-client/core";
import {
  FilterableDimensionConfig,
  ReportColumn,
  ReportColumnType,
  ReportConfiguration,
  ReportScope,
  TimeDimensionPosition,
  TimeDimensionType,
} from "@/reports/ReportModel";
import {
  MvProjectLinkAccessor,
  userLinkAccessorBuilder,
} from "@/reports/ReportLinkAccessors";
import { extractSortColumnsFromQuery } from "@/reports/organization/src/ReportUtils";

const query: Query = {
  measures: ["MvStudents.countryList"],
  order: {
    "MvStudents.WeEndsAt": "desc",
  },
  filters: [
    {
      member: "MvStudents.ProjectParticipationUserCategoryCode",
      operator: "equals",
      values: ["student"],
    },
  ],
  dimensions: [
    "MvStudents.ProjectParticipationUserCategory",
    "MvStudents.hash", // Required for LinkAccessor
    "MvStudents.availableForHireAt",
    "MvStudents.availableForInternshipAt",
    "MvStudents.name",
    "MvStudents.email",
    "MvStudents.universityName",
    "MvStudents.visa",
    "MvStudents.exportControlStatus",
    "MvStudents.current",
    "MvStudents.exitStatus",
    "MvStudents.ProjectParticipationDegreeDegree",
    "MvStudents.ProjectParticipationDegreeField",
    "MvStudents.ProjectParticipationDegreeGraduationDate",
    "SrcAccessCategoryTags.businessEntity",
    "SrcAccessCategoryTags.center",
    "SrcAccessCategoryTags.seismicshift",
    "SrcAccessCategoryTags.thrust",
    "SrcAccessCategoryTags.subthrust",
    "SrcAccessCategoryTags.theme",
    "SrcAccessCategoryTags.area",
    "SrcAccessCategoryTags.researchScholar",
    "SrcAccessCategoryTags.technicalTrack",
    "MvStudents.ProjectHash",
    "MvStudents.ProjectBusinessId",
    "MvStudents.ProjectName",
    "MvStudents.ProjectLeaderName",
    "MvStudents.ProjectLeaderHash",
    "MvStudents.ProjectStartsAt",
    "MvStudents.ProjectEndsAt",
    "MvStudents.ResumeName",
    "MvStudents.ResumeId",
  ],
  segments: ["MvStudents.notDeleted", "SrcAccessCategoryTags.notDeleted"],
};

// order is important
const filterableDimensions: FilterableDimensionConfig[] = [
  {
    dimension: "MvStudents.name",
    label: "User Name",
  },
  {
    dimension: "MvStudents.current",
    label: "Current",
  },
  {
    dimension: "MvStudents.exitStatus",
    label: "Student Exit Status",
  },
  {
    dimension: "MvStudents.country",
    label: "Citizenship",
  },
  {
    dimension: "MvStudents.visa",
    label: "US Visa Status",
  },
  {
    label: "Export Control Status",
    dimension: "MvStudents.exportControlStatus",
  },
  {
    dimension: "MvStudents.ProjectParticipationDegreeDegree",
    label: "Degree Type",
  },
  {
    dimension: "MvStudents.ProjectParticipationDegreeField",
    label: "Degree Field",
  },
  {
    dimension: "MvStudents.availableForHireAt",
    label: "Available for hire",
  },
  {
    dimension: "MvStudents.availableForInternshipAt",
    label: "Available for internship",
  },
];

// order is important
const columns: ReportColumn[] = [
  {
    title: "Student ID",
    key: "MvStudents.hash",
    type: ReportColumnType.Text,
  },
  {
    title: "Participation category",
    key: "MvStudents.ProjectParticipationUserCategory",
    type: ReportColumnType.Text,
  },
  {
    title: "Student name",
    key: "MvStudents.name",
    type: ReportColumnType.Link,
    linkAccessor: userLinkAccessorBuilder("MvStudents.hash"),
  },
  {
    title: "Student email",
    key: "MvStudents.email",
    type: ReportColumnType.Text,
  },
  {
    title: "Student University",
    key: "MvStudents.universityName",
    type: ReportColumnType.Text,
  },
  {
    title: "Visa",
    key: "MvStudents.visa",
    type: ReportColumnType.Text,
  },
  {
    title: "Degree Type",
    key: "MvStudents.ProjectParticipationDegreeDegree",
    type: ReportColumnType.Text,
  },
  {
    title: "Degree Field",
    key: "MvStudents.ProjectParticipationDegreeField",
    type: ReportColumnType.Text,
  },
  {
    title: "Degree Graduation Date",
    key: "MvStudents.ProjectParticipationDegreeGraduationDate",
    type: ReportColumnType.Date,
  },
  {
    title: "Resume",
    key: "MvStudents.ResumeName",
    fileIdMember: "MvStudents.ResumeId",
    type: ReportColumnType.File,
  },
  {
    title: "Citizenships",
    key: "MvStudents.countryList",
    type: ReportColumnType.Text,
  },
  {
    title: "US Visa status",
    key: "MvStudents.visa",
    type: ReportColumnType.Text,
  },
  {
    title: "Export Control Status",
    key: "MvStudents.exportControlStatus",
    type: ReportColumnType.Text,
  },
  {
    title: "Business Entity",
    key: "SrcAccessCategoryTags.businessEntity",
    type: ReportColumnType.Text,
  },
  {
    title: "Seismic Shift",
    key: "SrcAccessCategoryTags.seismicshift",
    type: ReportColumnType.Text,
  },
  {
    title: "Thrust",
    key: "SrcAccessCategoryTags.thrust",
    type: ReportColumnType.Text,
  },
  {
    title: "Subthrust",
    key: "SrcAccessCategoryTags.subthrust",
    type: ReportColumnType.Text,
  },
  {
    title: "Theme",
    key: "SrcAccessCategoryTags.theme",
    type: ReportColumnType.Text,
  },
  {
    title: "Center",
    key: "SrcAccessCategoryTags.center",
    type: ReportColumnType.Text,
  },
  {
    title: "Science Area",
    key: "SrcAccessCategoryTags.area",
    type: ReportColumnType.Text,
  },
  {
    title: "Research Scholar",
    key: "SrcAccessCategoryTags.researchScholar",
    type: ReportColumnType.Text,
  },
  {
    title: "Technical Track",
    key: "SrcAccessCategoryTags.technicalTrack",
    type: ReportColumnType.Text,
  },
  {
    title: "Project ID",
    key: "MvStudents.ProjectBusinessId",
    type: ReportColumnType.Numeric,
  },
  {
    title: "Project Pillar ID",
    key: "MvStudents.ProjectHash",
    type: ReportColumnType.Text,
  },
  {
    title: "Project Name",
    key: "MvStudents.ProjectName",
    type: ReportColumnType.Link,
    linkAccessor: MvProjectLinkAccessor,
  },
  {
    title: "Project Leader",
    key: "MvStudents.ProjectLeaderName",
    type: ReportColumnType.Link,
    linkAccessor: userLinkAccessorBuilder("MvStudents.ProjectLeaderHash"),
  },
  {
    title: "Project Start Date",
    key: "MvStudents.ProjectStartsAt",
    type: ReportColumnType.Date,
  },
  {
    title: "Projects End Date",
    key: "MvStudents.ProjectEndsAt",
    type: ReportColumnType.Date,
  },
];

export const SCH205SuperStudentParticipation: ReportConfiguration = {
  name: "Research Scholars (451M)",
  section: "Student",
  scope: ReportScope.Team,
  query,
  filterableDimensions,
  columns,
  hasDateFilters: true,
  timeDimensionType: TimeDimensionType.DayRange,
  timeDimension: {
    startsAt: "MvStudents.ProjectParticipationDegreeGraduationDate",
    endsAt: "MvStudents.ProjectParticipationDegreeGraduationDate",
  },
  sortColumns: extractSortColumnsFromQuery(query),
  timeDimensionPosition: TimeDimensionPosition.Top,
  zipExport: true,
};
